import { useState } from 'react';
import { UserPlus, UserMinus, AlertTriangle, Info, Building2, Users } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { EmployeeStatsCard } from './EmployeeStatsCard';
import { employeeService, EMPLOYEE_ROLES } from '@/services/tycoon/employeeService';
import { GAME_CONSTANTS } from '@/config/gameConstants';
import type { Employee, HiringProcess } from '@/types/tycoon';
import type { BuildingCapacity } from '@/types/buildings';
import type { EmployeeAllocation } from '@/services/tycoon/employeeAllocationService';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';

interface OperationsEmployeesProps {
  employees: Employee[];
  hiringProcesses: HiringProcess[];
  cash: number;
  reputation: number;
  showFireWarning: boolean;
  onHire: (role: string, count: number) => void;
  onFire: (role: string, count: number) => void;
  onToggleFireWarning: (show: boolean) => void;
  onNavigateToTab?: (tab: string) => void;
  buildingCapacity?: BuildingCapacity;
  checkCapacityForHiring?: (role: string, count: number, hiringProcesses?: HiringProcess[]) => {
    hasCapacity: boolean;
    availableCapacity: number;
    reason?: string;
  };
  employeeAllocations?: EmployeeAllocation[];
}

interface HireInputs {
  [key: string]: number;
}

interface FireInputs {
  [key: string]: number;
}

export function OperationsEmployees({
  employees,
  hiringProcesses,
  cash,
  reputation,
  showFireWarning,
  onHire,
  onFire,
  onToggleFireWarning,
  onNavigateToTab,
  buildingCapacity,
  checkCapacityForHiring,
  employeeAllocations = []
}: OperationsEmployeesProps) {
  // Initialize inputs for all employee roles dynamically
  const initialInputs = Object.keys(EMPLOYEE_ROLES).reduce((acc, role) => {
    acc[role] = 0;
    return acc;
  }, {} as HireInputs);

  const [hireInputs, setHireInputs] = useState<HireInputs>(initialInputs);
  const [fireInputs, setFireInputs] = useState<FireInputs>({...initialInputs});

  const [showFireModal, setShowFireModal] = useState(false);

  const stats = employeeService.getEmployeeStats(employees);

  // Helper function to get building category for a role
  const getRoleBuildingCategory = (role: string): 'office' | 'factory' | 'lab' | null => {
    // Direct mapping based on the actual game constants
    if (['engineer', 'researcher'].includes(role)) {
      return 'lab'; // Technical workers go to lab
    } else if (['designer', 'marketer', 'manager'].includes(role)) {
      return 'office'; // Office workers
    } else if (['assemblyWorker', 'tester'].includes(role)) {
      return 'factory'; // Factory workers
    }
    
    return null;
  };

  // Helper to get available capacity for a role
  const getAvailableCapacityForRole = (role: string): number => {
    if (!buildingCapacity || !checkCapacityForHiring) return 0;
    
    const capacityCheck = checkCapacityForHiring(role, 0, hiringProcesses);
    return capacityCheck.availableCapacity || 0;
  };

  // Calculate available employees considering allocations
  const getAvailableEmployeesByRole = (role: string): number => {
    const totalEmployees = employees.filter(emp => emp.role === role).length;
    
    // Count allocated employees for this role
    const allocatedCount = employeeAllocations
      .filter(allocation => allocation.employeeType === role)
      .reduce((sum, allocation) => sum + allocation.count, 0);
    
    return Math.max(0, totalEmployees - allocatedCount);
  };

  const handleHireInputChange = (role: string, delta: number) => {
    setHireInputs(prev => {
      const newValue = Math.max(0, prev[role] + delta);
      
      // Check category capacity including pending hires
      const category = getRoleBuildingCategory(role);
      if (category && buildingCapacity) {
        const categoryCapacity = buildingCapacity.byCategory[category];
        
        // Check if categoryCapacity exists
        if (categoryCapacity) {
          // Calculate pending hires in this category
          let pendingHiresInCategory = 0;
          if (hiringProcesses && hiringProcesses.length > 0) {
            hiringProcesses.forEach(process => {
              const processCategory = getRoleBuildingCategory(process.role);
              if (processCategory === category) {
                pendingHiresInCategory += process.count;
              }
            });
          }
          
          // Calculate other roles in the same category from current form
          const otherRolesInCategory = Object.entries(prev)
            .filter(([r]) => r !== role && getRoleBuildingCategory(r) === category)
            .reduce((sum, [, count]) => sum + count, 0);
          
          // Available = total - used - pending - other roles in form
          const categoryAvailable = categoryCapacity ? categoryCapacity.total - categoryCapacity.used - pendingHiresInCategory - otherRolesInCategory : 0;
          const maxAllowed = Math.max(0, categoryAvailable);
          
          return {
            ...prev,
            [role]: Math.min(newValue, maxAllowed)
          };
        }
      }
      
      return {
        ...prev,
        [role]: newValue
      };
    });
  };

  const handleFireInputChange = (role: string, delta: number) => {
    const maxCount = stats[role] || 0;
    
    setFireInputs(prev => ({
      ...prev,
      [role]: Math.max(0, Math.min(maxCount, prev[role] + delta))
    }));
  };

  const handleHire = () => {
    // Validate total capacity before hiring
    const totalToHire = Object.values(hireInputs).reduce((sum, count) => sum + count, 0);
    if (totalToHire === 0) return;
    
    // Group hires by category and check capacity
    const hiresByCategory: Record<string, number> = {};
    Object.entries(hireInputs).forEach(([role, count]) => {
      if (count > 0) {
        const category = getRoleBuildingCategory(role);
        if (category) {
          hiresByCategory[category] = (hiresByCategory[category] || 0) + count;
        }
      }
    });
    
    // Check if any category would exceed capacity including pending hires
    if (buildingCapacity && checkCapacityForHiring) {
      for (const [category, totalCount] of Object.entries(hiresByCategory)) {
        const categoryCapacity = buildingCapacity.byCategory[category as 'office' | 'factory' | 'lab'];
        if (categoryCapacity) {
          // Calculate pending hires in this category
          let pendingHiresInCategory = 0;
          if (hiringProcesses && hiringProcesses.length > 0) {
            hiringProcesses.forEach(process => {
              const processCategory = getRoleBuildingCategory(process.role);
              if (processCategory === category) {
                pendingHiresInCategory += process.count;
              }
            });
          }
          
          const availableSlots = categoryCapacity ? categoryCapacity.total - categoryCapacity.used - pendingHiresInCategory : 0;
          if (totalCount > availableSlots) {
            // Don't proceed if it would exceed capacity
            return;
          }
        }
      }
    }
    
    // Proceed with hiring
    Object.entries(hireInputs).forEach(([role, count]) => {
      if (count > 0) {
        onHire(role, count);
      }
    });
    
    // Reset inputs
    setHireInputs(initialInputs);
  };

  const calculateHiringCost = () => {
    return Object.entries(hireInputs).reduce((total, [role, count]) => {
      const roleData = EMPLOYEE_ROLES[role];
      if (!roleData || !roleData.baseSalary || count <= 0) {
        return total;
      }
      const monthlySalary = roleData.baseSalary * 30;
      return total + (monthlySalary * count);
    }, 0);
  };

  const calculateFirePreview = () => {
    const totalToFire = Object.values(fireInputs).reduce((sum, count) => sum + count, 0);
    const employeesToFire: Employee[] = [];
    
    Object.entries(fireInputs).forEach(([role, count]) => {
      const roleEmployees = employees.filter(e => e.role === role);
      employeesToFire.push(...roleEmployees.slice(0, count));
    });
    
    const severanceCost = employeeService.calculateSeveranceCost(employeesToFire) || 0;
    const reputationImpact = employeeService.calculateReputationImpact(totalToFire) || 0;
    
    return {
      count: totalToFire,
      severanceCost: isNaN(severanceCost) || !isFinite(severanceCost) ? 0 : severanceCost,
      reputationImpact: isNaN(reputationImpact) || !isFinite(reputationImpact) ? 0 : reputationImpact
    };
  };

  const handleFireAction = () => {
    const preview = calculateFirePreview();
    
    if (preview.count === 0) return;
    
    if (showFireWarning) {
      setShowFireModal(true);
    } else {
      executeFire();
    }
  };

  const executeFire = () => {
    Object.entries(fireInputs).forEach(([role, count]) => {
      if (count > 0) {
        onFire(role, count);
      }
    });
    
    // Reset inputs
    setFireInputs({...initialInputs});
    
    setShowFireModal(false);
  };

  const hiringCost = calculateHiringCost();
  const firePreview = calculateFirePreview();
  
  // Calculate if hiring would exceed capacity
  const hiresByCategory = Object.entries(hireInputs).reduce((acc, [role, count]) => {
    if (count > 0) {
      const category = getRoleBuildingCategory(role);
      if (category) {
        acc[category] = (acc[category] || 0) + count;
      }
    }
    return acc;
  }, {} as Record<string, number>);
  
  const wouldExceedCapacity = buildingCapacity ? Object.entries(hiresByCategory).some(([category, totalCount]) => {
    const categoryCapacity = buildingCapacity.byCategory[category as 'office' | 'factory' | 'lab'];
    if (!categoryCapacity) return false;
    
    // Calculate pending hires in this category
    let pendingHiresInCategory = 0;
    if (hiringProcesses && hiringProcesses.length > 0) {
      hiringProcesses.forEach(process => {
        const processCategory = getRoleBuildingCategory(process.role.toLowerCase());
        if (processCategory === category) {
          pendingHiresInCategory += process.count;
        }
      });
    }
    
    return categoryCapacity ? totalCount > (categoryCapacity.total - categoryCapacity.used - pendingHiresInCategory) : false;
  }) : false;
  
  const canHire = hiringCost <= cash &&
                  Object.values(hireInputs).some(v => v > 0) &&
                  !wouldExceedCapacity;
  const canFire = firePreview.severanceCost <= cash && firePreview.count > 0;

  const totalEmployees = employees.length;
  const showFireSection = totalEmployees > 0;

  // Group roles by building category for progressive disclosure
  const roleGroups = [
    { name: 'R&D Lab', category: 'lab' as const, roles: Object.entries(EMPLOYEE_ROLES).filter(([key]) => getRoleBuildingCategory(key) === 'lab') },
    { name: 'Office', category: 'office' as const, roles: Object.entries(EMPLOYEE_ROLES).filter(([key]) => getRoleBuildingCategory(key) === 'office') },
    { name: 'Production', category: 'factory' as const, roles: Object.entries(EMPLOYEE_ROLES).filter(([key]) => getRoleBuildingCategory(key) === 'factory') },
  ];

  return (
    <div className={`grid ${showFireSection ? 'grid-cols-3' : 'grid-cols-2'} gap-6 h-full`}>
      {/* Left Column - Employee Stats */}
      <div className="space-y-6">
        <EmployeeStatsCard
          employees={employees}
          onClick={() => onNavigateToTab?.('overview')}
        />

        {/* Available Employees Card - only show when there are employees */}
        {totalEmployees > 0 && (
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-theme-success" />
            <h3 className="text-lg font-semibold text-theme-text-primary">Available Employees</h3>
          </div>

          <div className="space-y-2">
            {Object.entries(EMPLOYEE_ROLES)
              .filter(([, role]) => !role.disabled)
              .map(([roleKey, role]) => {
                const available = getAvailableEmployeesByRole(roleKey);
                const total = stats[roleKey] || 0;
                const allocated = total - available;

                return (
                  <div key={roleKey} className="flex justify-between items-center py-1">
                    <span className="text-theme-text-secondary text-sm">{role.name}s</span>
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${available === 0 ? 'text-theme-error' : 'text-theme-success'}`}>
                        {available}
                      </span>
                      {allocated > 0 && (
                        <span className="text-xs text-theme-text-muted">
                          ({allocated} allocated)
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>

          {employeeAllocations.length > 0 && (
            <div className="mt-3 pt-3 border-t border-theme-border">
              <p className="text-xs text-theme-text-muted">
                {employeeAllocations.length} active allocation{employeeAllocations.length > 1 ? 's' : ''}
              </p>
            </div>
          )}
        </Card>
        )}

        {/* Building Capacity - show when no employees for first-time context */}
        {totalEmployees === 0 && buildingCapacity && (
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-4 h-4 text-theme-info" />
              <span className="text-sm font-medium text-theme-text-primary">Building Capacity</span>
            </div>
            <div className="space-y-1 text-xs text-theme-text-secondary">
              {(['office', 'factory', 'lab'] as const).map(category => {
                const cap = buildingCapacity.byCategory[category];
                if (!cap) return null;
                return (
                  <div key={category} className="flex justify-between">
                    <span>{category.charAt(0).toUpperCase() + category.slice(1)}: {cap.used}/{cap.total}</span>
                    <span>({cap.total - cap.used} available)</span>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>

      {/* Middle Column - Hire Controls */}
      <div className="space-y-6">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus className="w-5 h-5 text-theme-success" />
            <h3 className="text-lg font-semibold text-theme-text-primary">Hire Employees</h3>
          </div>

          {/* Grouped by building category */}
          <div className="space-y-5">
            {roleGroups.map(group => {
              const groupCap = buildingCapacity?.byCategory[group.category];
              let pendingInGroup = 0;
              if (hiringProcesses?.length > 0) {
                hiringProcesses.forEach(process => {
                  if (getRoleBuildingCategory(process.role) === group.category) {
                    pendingInGroup += process.count;
                  }
                });
              }
              const groupAvailable = groupCap ? groupCap.total - groupCap.used - pendingInGroup : 0;

              return (
                <div key={group.category}>
                  {/* Category header with capacity */}
                  <div className="flex items-center justify-between mb-2 pb-1 border-b border-theme-border">
                    <div className="flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5 text-theme-text-muted" />
                      <span className="text-xs font-semibold uppercase tracking-wider text-theme-text-muted">
                        {group.name}
                      </span>
                    </div>
                    {groupCap && (
                      <span className={`text-xs ${groupAvailable === 0 ? 'text-theme-error' : 'text-theme-text-muted'}`}>
                        {groupAvailable} slots
                        {pendingInGroup > 0 && <span className="text-theme-warning"> ({pendingInGroup} pending)</span>}
                      </span>
                    )}
                  </div>

                  {/* Roles in this category */}
                  <div className="space-y-2">
                    {group.roles.map(([roleKey, role]) => {
                      const availableCapacity = getAvailableCapacityForRole(roleKey);
                      const isDisabled = role.disabled ?? false;
                      const canHireAny = availableCapacity > 0 && !isDisabled;

                      return (
                        <div key={roleKey} className="flex items-center justify-between">
                          <span className={`text-sm text-theme-text-secondary ${isDisabled ? 'opacity-50' : ''}`}>
                            {role.name}
                            {isDisabled && <span className="text-xs text-theme-text-muted ml-1">({role.disabledReason})</span>}
                          </span>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleHireInputChange(roleKey, -1)}
                              disabled={hireInputs[roleKey] === 0 || isDisabled}
                            >
                              -
                            </Button>
                            <input
                              type="number"
                              data-testid={`hire-count-${roleKey}`}
                              value={hireInputs[roleKey]}
                              onChange={(e) => {
                                const value = parseInt(e.target.value) || 0;
                                const cat = getRoleBuildingCategory(roleKey);
                                let maxAllowed = availableCapacity;
                                if (cat && buildingCapacity) {
                                  const catCap = buildingCapacity.byCategory[cat];
                                  if (catCap) {
                                    let pending = 0;
                                    hiringProcesses?.forEach(p => {
                                      if (getRoleBuildingCategory(p.role) === cat) pending += p.count;
                                    });
                                    const otherInCat = Object.entries(hireInputs)
                                      .filter(([r]) => r !== roleKey && getRoleBuildingCategory(r) === cat)
                                      .reduce((s, [, c]) => s + c, 0);
                                    maxAllowed = Math.min(maxAllowed, catCap.total - catCap.used - pending - otherInCat);
                                  }
                                }
                                setHireInputs(prev => ({ ...prev, [roleKey]: Math.max(0, Math.min(value, maxAllowed)) }));
                              }}
                              className={`w-16 text-center border rounded px-2 py-1 text-theme-text-primary bg-theme-bg-secondary ${
                                !canHireAny ? 'border-theme-error bg-theme-error/10' : 'border-theme-border'
                              }`}
                              min="0"
                              max={availableCapacity}
                              placeholder="0"
                              disabled={!canHireAny}
                            />
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleHireInputChange(roleKey, 1)}
                              disabled={hireInputs[roleKey] >= availableCapacity || isDisabled}
                            >
                              +
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {groupAvailable === 0 && (
                    <div className="flex items-center gap-2 p-2 mt-2 bg-theme-error/10 rounded-lg text-xs">
                      <AlertTriangle className="w-3 h-3 text-theme-error flex-shrink-0" />
                      <span className="text-theme-error">
                        No {group.name.toLowerCase()} capacity.{' '}
                        <button onClick={() => onNavigateToTab?.('buildings')} className="underline hover:opacity-80">
                          Purchase buildings
                        </button>
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {wouldExceedCapacity && Object.values(hireInputs).some(v => v > 0) && (
            <div className="mt-2 p-2 bg-theme-error/10 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-theme-error flex-shrink-0" />
                <span className="text-xs text-theme-error">
                  Total employees selected would exceed building capacity
                </span>
              </div>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-theme-border">
            <div className="flex justify-between items-center mb-3">
              <span className="text-theme-text-secondary">Upfront Cost:</span>
              <span className="font-medium">{formatCurrency(hiringCost)}</span>
            </div>
            <Button
              onClick={handleHire}
              disabled={!canHire}
              data-testid="hire-button"
              className="w-full"
            >
              Hire Selected Employees
            </Button>
            <p className="text-xs text-theme-text-muted mt-2">
              Hiring takes {GAME_CONSTANTS.employee.hiringDays} days to complete
            </p>
          </div>
        </Card>

        {/* Active Hiring Processes */}
        {hiringProcesses.length > 0 && (
          <Card className="p-6">
            <h4 className="text-lg font-semibold mb-4 text-theme-text-primary">Hiring in Progress</h4>
            <div className="space-y-3">
              {hiringProcesses.map((process, idx) => (
                <div key={idx}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-theme-text-secondary">
                      {process.count} {EMPLOYEE_ROLES[process.role]?.name}(s)
                    </span>
                    <span className="text-theme-text-muted text-sm">{process.daysRemaining} days left</span>
                  </div>
                  <div className="w-full bg-theme-bg-tertiary rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all"
                      style={{
                        width: `${((GAME_CONSTANTS.employee.hiringDays - process.daysRemaining) / GAME_CONSTANTS.employee.hiringDays) * 100}%`
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Right Column - Fire Controls (hidden when no employees) */}
      {showFireSection && (
      <div>
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <UserMinus className="w-5 h-5 text-theme-error" />
            <h3 className="text-lg font-semibold text-theme-text-primary">Fire Employees</h3>
          </div>

          <div className="space-y-3">
            {Object.entries(EMPLOYEE_ROLES)
              .filter(([roleKey]) => (stats[roleKey] || 0) > 0)
              .map(([roleKey, role]) => {
              const max = stats[roleKey] || 0;
              const isDisabled = role.disabled ?? false;

              return (
                <div key={roleKey} className="flex items-center justify-between">
                  <span className={`text-theme-text-secondary ${isDisabled ? 'opacity-50' : ''}`}>
                    {role.name} <span className="text-theme-text-muted text-sm">(max: {max})</span>
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleFireInputChange(roleKey, -1)}
                      disabled={fireInputs[roleKey] === 0 || isDisabled}
                    >
                      -
                    </Button>
                    <input
                      type="number"
                      data-testid={`fire-count-${roleKey}`}
                      value={fireInputs[roleKey]}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        setFireInputs(prev => ({
                          ...prev,
                          [roleKey]: Math.max(0, Math.min(max, value))
                        }));
                      }}
                      className="w-16 text-center border border-theme-border rounded px-2 py-1 text-theme-text-primary bg-theme-bg-secondary"
                      min="0"
                      max={max}
                      placeholder="0"
                      disabled={isDisabled}
                    />
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleFireInputChange(roleKey, 1)}
                      disabled={fireInputs[roleKey] >= max || isDisabled}
                    >
                      +
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {firePreview.count > 0 && (
            <div className="mt-4 p-3 bg-theme-error/10 rounded-lg">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-theme-error">Severance Cost:</span>
                  <span className="font-medium text-theme-error">
                    {formatCurrency(firePreview.severanceCost)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-theme-error">Reputation Impact:</span>
                  <span className="font-medium text-theme-error">
                    -{(firePreview.reputationImpact || 0).toFixed(1)}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-theme-border">
            <Button
              onClick={handleFireAction}
              disabled={!canFire}
              variant="danger"
              data-testid="fire-button"
              className="w-full"
            >
              Fire Selected Employees
            </Button>
          </div>
        </Card>
      </div>
      )}

      {/* Fire Warning Modal */}
      <Modal
        isOpen={showFireModal}
        onClose={() => setShowFireModal(false)}
        size="md"
        data-testid="fire-confirmation"
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-8 h-8 text-theme-error" />
            <h2 className="text-xl font-bold text-theme-text-primary">Confirm Employee Termination</h2>
          </div>

          <div className="space-y-4">
            <p className="text-theme-text-secondary">
              You are about to fire {firePreview.count} employee{firePreview.count > 1 ? 's' : ''}.
              This action will have the following effects:
            </p>

            <div className="bg-theme-error/10 p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-theme-error">Severance Cost:</span>
                <span className="font-medium text-theme-error">
                  {formatCurrency(firePreview.severanceCost)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-theme-error">Reputation Loss:</span>
                <span className="font-medium text-theme-error">
                  -{(firePreview.reputationImpact || 0).toFixed(1)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-theme-error">New Reputation:</span>
                <span className="font-medium text-theme-error">
                  {Math.max(0, reputation - (firePreview.reputationImpact || 0)).toFixed(1)}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="dontWarnAgain"
                onChange={(e) => onToggleFireWarning(!e.target.checked)}
                className="rounded border-theme-border"
              />
              <label htmlFor="dontWarnAgain" className="text-sm text-theme-text-secondary">
                Do not warn me again
              </label>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <Button
              onClick={() => setShowFireModal(false)}
              variant="secondary"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={executeFire}
              variant="danger"
              data-testid="confirm-fire"
              className="flex-1"
            >
              Confirm Termination
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}